import {
  Button,
  Text,
  HStack,
  Box,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogPositioner,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  title: string
  message: string | ReactNode
  confirmLabel?: string
  cancelLabel?: string
  colorPalette?: 'brand' | 'blue' | 'green' | 'orange' | 'red' | 'gray' | 'purple'
  icon?: ReactNode
  isLoading?: boolean
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  colorPalette = 'brand',
  icon,
  isLoading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation()

  const handleConfirm = async () => {
    await onConfirm()
    onClose()
  }

  const colorMap: Record<string, { bg: string; border: string; iconColor: string }> = {
    brand: { bg: 'brand.50', border: 'brand.200', iconColor: 'brand.600' },
    blue: { bg: 'blue.50', border: 'blue.200', iconColor: 'blue.600' },
    green: { bg: 'green.50', border: 'green.200', iconColor: 'green.600' },
    orange: { bg: 'orange.50', border: 'orange.200', iconColor: 'orange.600' },
    red: { bg: 'red.50', border: 'red.200', iconColor: 'red.600' },
    gray: { bg: 'gray.50', border: 'gray.200', iconColor: 'gray.600' },
    purple: { bg: 'purple.50', border: 'purple.200', iconColor: 'purple.600' },
  }

  const colors = colorMap[colorPalette] ?? colorMap.brand!

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="450px">
          <DialogHeader>
            <DialogTitle display="flex" alignItems="center" gap={2}>
              {icon && <Box color={colors.iconColor}>{icon}</Box>}
              <span>{title}</span>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            {typeof message === 'string' ? <Text>{message}</Text> : message}
          </DialogBody>

          <DialogFooter>
            <HStack gap={2}>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelLabel || t('common.cancel')}
              </Button>
              <Button
                colorPalette={colorPalette}
                onClick={handleConfirm}
                loading={isLoading}
              >
                {confirmLabel || t('common.confirm')}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
